
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { VoiceCommand, VoiceCommandState, VoiceCommandContextType } from '@/types/voiceCommand';

const VoiceCommandContext = createContext<VoiceCommandContextType | undefined>(undefined);

export const VoiceCommandProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [commands, setCommands] = useState<VoiceCommand[]>([]);
  const [state, setState] = useState<VoiceCommandState>({
    isListening: false,
    isProcessing: false,
    recognizedText: '',
    showCommandList: false
  });

  // Reference to the speech recognition instance
  const recognitionRef = React.useRef<SpeechRecognition | null>(null);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognitionAPI) {
      recognitionRef.current = new SpeechRecognitionAPI();
      const recognition = recognitionRef.current;
      
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      
      recognition.onstart = () => {
        setState(prev => ({ ...prev, isListening: true, error: undefined }));
      };
      
      recognition.onend = () => {
        setState(prev => ({ ...prev, isListening: false }));
      };
      
      recognition.onerror = (event) => {
        setState(prev => ({ 
          ...prev, 
          isListening: false, 
          error: event.error 
        }));
        
        toast({
          title: "Voice Command Error",
          description: `Error: ${event.error}. Please try again.`,
          variant: "destructive"
        });
      };
      
      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        
        setState(prev => ({ ...prev, recognizedText: transcript }));
        
        // Check if this is a final result
        if (event.results[0].isFinal) {
          processCommand(transcript);
        }
      };
    } else {
      toast({
        title: "Voice Commands Not Supported",
        description: "Your browser doesn't support voice recognition. Please try a different browser.",
        variant: "destructive"
      });
    }
    
    // Clean up
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [toast]);
  
  // Register default navigation commands
  useEffect(() => {
    const defaultCommands: VoiceCommand[] = [
      {
        name: 'navigate',
        phrases: ['go to', 'open', 'navigate to', 'show me'],
        category: 'navigation',
        action: (destination) => {
          if (destination) {
            const routes: Record<string, string> = {
              'home': '/',
              'dashboard': '/dashboard',
              'calendar': '/calendar-scheduling',
              'schedule': '/calendar-scheduling',
              'releases': '/releases',
              'library': '/library',
              'network': '/musicians-network',
              'musicians': '/musicians-network',
              'analytics': '/analytics',
              'marketing': '/marketing',
              'marketplace': '/marketplace',
              'royalties': '/royalties',
              'music rights': '/music-rights-admin',
              'streaming': '/live-streaming',
              'legal': '/legal-tools',
              'gigs': '/gig-management',
              'settings': '/settings',
              'collaboration': '/collaboration',
              'profile': '/profile',
              'awards': '/award-networking',
              'learning': '/learning',
              'funding': '/funding',
              'progress': '/artist-progress'
            };
            
            // Find the closest match for the destination
            const destinationKey = Object.keys(routes).find(key => 
              destination.toLowerCase().includes(key)
            );
            
            if (destinationKey) {
              navigate(routes[destinationKey]);
              setState(prev => ({ 
                ...prev, 
                lastCommand: `navigate to ${destinationKey}`,
                lastResult: `Navigated to ${destinationKey}` 
              }));
              
              toast({
                title: "Navigation",
                description: `Navigating to ${destinationKey}`
              });
            } else {
              toast({
                title: "Navigation Error",
                description: `I couldn't find a page called "${destination}"`,
                variant: "destructive"
              });
            }
          }
        },
        description: "Navigate to a different page",
        examples: ["Go to dashboard", "Open calendar", "Show me analytics"]
      },
      {
        name: 'help',
        phrases: ['help', 'show commands', 'what can i say', 'voice commands'],
        category: 'general',
        action: () => {
          setState(prev => ({ ...prev, showCommandList: true }));
        },
        description: "Display available voice commands",
        examples: ["Help", "Show commands", "What can I say"]
      }
    ];
    
    setCommands(prev => [...prev, ...defaultCommands]);
  }, [navigate, toast]);
  
  // Process recognized speech and execute matching commands
  const processCommand = useCallback((transcript: string) => {
    if (!transcript) return;
    
    setState(prev => ({ ...prev, isProcessing: true }));
    
    const lowerTranscript = transcript.toLowerCase();
    
    // Try to match the transcript to a command
    for (const command of commands) {
      for (const phrase of command.phrases) {
        if (lowerTranscript.includes(phrase.toLowerCase())) {
          // Extract the parameter (text after the phrase)
          const phraseIndex = lowerTranscript.indexOf(phrase.toLowerCase());
          const param = transcript
            .slice(phraseIndex + phrase.length)
            .trim();
          
          // Execute the command action
          command.action(param);
          
          setState(prev => ({ 
            ...prev, 
            isProcessing: false,
            lastCommand: transcript,
            lastResult: `Executed ${command.name}` + (param ? ` with "${param}"` : '')
          }));
          
          return;
        }
      }
    }
    
    // No command matched
    setState(prev => ({ 
      ...prev, 
      isProcessing: false,
      lastCommand: transcript,
      lastResult: "No matching command found"
    }));
    
    toast({
      title: "Voice Command",
      description: "I didn't understand that command. Try saying 'help' to see available commands.",
      variant: "destructive"
    });
  }, [commands, toast]);
  
  // Start listening for commands
  const startListening = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
        toast({
          title: "Voice Commands",
          description: "Listening for commands..."
        });
      } catch (error) {
        console.error('Recognition error:', error);
      }
    }
  }, [toast]);
  
  // Stop listening for commands
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  }, []);
  
  // Toggle listening state
  const toggleListening = useCallback(() => {
    if (state.isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [state.isListening, startListening, stopListening]);
  
  // Register a new command
  const registerCommand = useCallback((command: VoiceCommand) => {
    setCommands(prev => {
      // Check if command already exists
      const existingIndex = prev.findIndex(c => c.name === command.name);
      if (existingIndex >= 0) {
        // Replace existing command
        const newCommands = [...prev];
        newCommands[existingIndex] = command;
        return newCommands;
      }
      // Add new command
      return [...prev, command];
    });
  }, []);
  
  // Unregister a command
  const unregisterCommand = useCallback((commandName: string) => {
    setCommands(prev => prev.filter(c => c.name !== commandName));
  }, []);
  
  // Toggle command list visibility
  const toggleCommandList = useCallback(() => {
    setState(prev => ({ ...prev, showCommandList: !prev.showCommandList }));
  }, []);
  
  // Memoize context value
  const contextValue = useMemo(() => ({
    commands,
    state,
    startListening,
    stopListening,
    toggleListening,
    registerCommand,
    unregisterCommand,
    toggleCommandList,
  }), [
    commands, 
    state, 
    startListening, 
    stopListening, 
    toggleListening,
    registerCommand,
    unregisterCommand,
    toggleCommandList
  ]);
  
  return (
    <VoiceCommandContext.Provider value={contextValue}>
      {children}
    </VoiceCommandContext.Provider>
  );
};

export const useVoiceCommands = (): VoiceCommandContextType => {
  const context = useContext(VoiceCommandContext);
  if (context === undefined) {
    throw new Error('useVoiceCommands must be used within a VoiceCommandProvider');
  }
  return context;
};
